
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

const DTICalculator = () => {
  const [monthlyIncome, setMonthlyIncome] = useState<string>('5000');
  const [monthlyDebtPayments, setMonthlyDebtPayments] = useState<string>('1200');
  const [dti, setDTI] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const calculateDTI = () => {
    try {
      setError(null);
      
      const income = parseFloat(monthlyIncome);
      const debt = parseFloat(monthlyDebtPayments);

      if (isNaN(income) || isNaN(debt)) {
        setError("Please enter valid numbers");
        return;
      }

      if (income <= 0) {
        setError("Monthly income must be positive");
        return;
      }

      if (debt < 0) {
        setError("Monthly debt payments cannot be negative");
        return;
      }

      // DTI = (Total monthly debt payments / Gross monthly income) × 100
      const dtiValue = (debt / income) * 100;
      setDTI(dtiValue);

    } catch (err) {
      setError("Error calculating DTI");
    }
  };

  const getDTIRating = (dti: number) => {
    if (dti <= 20) return { rating: "Excellent", color: "text-green-600" };
    if (dti <= 36) return { rating: "Good", color: "text-blue-600" };
    if (dti <= 50) return { rating: "Fair", color: "text-yellow-600" };
    return { rating: "Poor", color: "text-red-600" };
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardContent className="p-6">
          <h2 className="text-xl font-semibold mb-4">DTI (Debt-to-Income) Calculator</h2>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="monthly-income">Gross Monthly Income ($)</Label>
              <Input
                id="monthly-income"
                type="number"
                value={monthlyIncome}
                onChange={(e) => setMonthlyIncome(e.target.value)}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="monthly-debt">Total Monthly Debt Payments ($)</Label>
              <Input
                id="monthly-debt"
                type="number"
                value={monthlyDebtPayments}
                onChange={(e) => setMonthlyDebtPayments(e.target.value)}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                Include credit cards, loans, mortgage, etc.
              </p>
            </div>

            <Button onClick={calculateDTI} className="w-full">
              Calculate DTI
            </Button>

            {error && (
              <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm">
                {error}
              </div>
            )}

            {dti !== null && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-medium">Debt-to-Income Ratio</h3>
                <p className="text-2xl font-bold">{dti.toFixed(1)}%</p>
                {(() => {
                  const rating = getDTIRating(dti);
                  return (
                    <p className={`text-lg font-semibold ${rating.color}`}>
                      {rating.rating}
                    </p>
                  );
                })()}
                <div className="mt-4 text-sm">
                  <p><strong>Guidelines:</strong></p>
                  <ul className="list-disc pl-5 mt-1">
                    <li>&le;20%: Excellent - Very low debt burden</li>
                    <li>21-36%: Good - Manageable debt level</li>
                    <li>37-50%: Fair - Consider debt reduction</li>
                    <li>&gt;50%: Poor - Significant debt burden</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DTICalculator;
